export class AzureAdConditionsService {
	static $inject = [
		'mx.internationalization',
		'mx.SolutionBuilderAgent.ConditionsBuilder.Builder',
		'mx.SolutionBuilderAgent.ConditionsBuilder.Enums',
		'mx.sb.Agent.DataQuery.FilterOperatorsFactory',
		'mx.SolutionBuilderAgent.EntityService',
	];

	conditionsBuilder;
	conditionsBuilderEnums;
	filterOperatorsFactory;
	entityService;

	ConditionLogicOperators;
	ConditionValueTypes;
	ConditionTypes;
	ConditionBuilderMode;

	i18nBuilder;
	localEnumLoader;

	constructor(i18n, conditionsBuilder, conditionsBuilderEnums, filterOperatorsFactory, entityService) {
		this.conditionsBuilder = conditionsBuilder;
		this.conditionsBuilderEnums = conditionsBuilderEnums;
		this.filterOperatorsFactory = filterOperatorsFactory;
		this.entityService = entityService;

		this.ConditionLogicOperators = this.conditionsBuilderEnums.ConditionLogicOperators;
		this.ConditionValueTypes = this.conditionsBuilderEnums.ConditionValueTypes;
		this.ConditionTypes = this.conditionsBuilderEnums.ConditionTypes;
		this.ConditionBuilderMode = this.conditionsBuilderEnums.ConditionBuilderMode;

		this.i18nBuilder = i18n.get('solutionBuilderAgent.components.mx-conditions-builder');
		this.localEnumLoader = async (condition) => await this.enumLoader(condition);
	}

	getBuilder(inputProperties, query) {
		const config = {
			logicOperators: {
				enumeration: [
					{
						title: this.i18nBuilder.and,
						value: this.ConditionLogicOperators.AND,
					},
					{
						title: this.i18nBuilder.or,
						value: this.ConditionLogicOperators.OR,
					},
				],
			},
			valueTypes: {
				enumeration: [
					{
						title: this.i18nBuilder.condition,
						value: this.ConditionValueTypes.Value,
					},
				],
			},
			startConditions: {
				enumeration: [],
			},
		};

		config.conditionOutput = this.conditionOutput;
		config.groupOutput = this.groupOutput;
		config.builderOutput = this.builderOutput;
		config.operators = [];
		config.maxLevel = 2;
		config.availableDateMods = [this.conditionsBuilderEnums.ConditionDateMods.Date];

		config.hidePropertiesFilter = true;
		config.mode = this.ConditionBuilderMode.disableDifferentOperatorsInGroup;

		let properties = [];
		try {
			properties = JSON.parse(inputProperties).map((property) => {
				const prop = {
					ValueType: this.ConditionValueTypes.Value,
					ConditionType: property.Type,
					Title: property.Name,
					Name: property.Name,
					Operators: this.setupOperators(property),
				};

				if (prop.ConditionType === this.ConditionTypes.EnumerationReference) {
					prop.ReferenceTo = property.ReferenceTo;
					prop.ReferenceFilter = property.ReferenceFilter;
					prop.EnumLoader = this.localEnumLoader;
				}
				return prop;
			});
		} catch (e) {
			console.error(e.message);
		}

		config.predefinedConditions = this.inputMapping(query, properties);
		const builder = new this.conditionsBuilder(config);
		builder.properties = properties;
		return builder;
	}

	/*private*/
	setupOperators(property) {
		let operators = null;
		if (property.ConditionOperators && property.ConditionOperators.length > 0) {
			operators = [];
			const operatorIDs = property.ConditionOperators.split(',').map((operatorString) => parseInt(operatorString.trim()));

			if (property.IsNullable) {
				if (!operatorIDs.includes(this.filterOperatorsFactory.operators.IsSet.value)) {
					operatorIDs.push(this.filterOperatorsFactory.operators.IsSet.value);
				}
				if (!operatorIDs.includes(this.filterOperatorsFactory.operators.IsNotSet.value)) {
					operatorIDs.push(this.filterOperatorsFactory.operators.IsNotSet.value);
				}
			}

			operatorIDs.forEach((filterOperator) => {
				Object.keys(this.filterOperatorsFactory.operators).forEach((key) => {
					const operator = this.filterOperatorsFactory.operators[key];
					if (operator.value === filterOperator) {
						operators.push({
							title: operator.name,
							value: operator.value,
							isArray: operator.isArray || false,
							unconfigurableValue: operator.withoutParams || false,
						});
					}
				});
			});
		}
		return operators;
	}

	/*private*/
	async enumLoader(condition) {
		const enumerationValues = await this.entityService.getEnumerationValues(condition._options.ReferenceTo, condition._options.ReferenceFilter);

		return enumerationValues
			.map(function (item) {
				return {
					title: item.DisplayString,
					value: item.Value,
				};
			})
			.sort(function (a, b) {
				return ('' + a.title).localeCompare(b.title);
			});
	}

	/*private*/
	inputMapping(query, properties) {
		let rootGroup = {};
		try {
			rootGroup = JSON.parse(query);
		} catch (e) {
			mx.logCatch(e);
		}

		return this.createInputMapper(properties)(rootGroup);
	}

	/*private*/
	createInputMapper(properties) {
		const types = {};
		properties.forEach((property) => {
			const prop = {
				ConditionType: property.ConditionType,
				Operators: property.Operators,
			};

			if (prop.ConditionType === this.ConditionTypes.EnumerationReference) {
				prop.ReferenceTo = property.ReferenceTo;
				prop.ReferenceFilter = property.ReferenceFilter;
			}

			types[property.Name] = prop;
		});

		const groupInput = function (group) {
			if (typeof group !== 'object' || group === null) {
				return {};
			}

			const inputGroup = {
				Groups: Array.isArray(group.Groups) ? group.Groups.map(groupInput) : [],
				Conditions: Array.isArray(group.Conditions) ? group.Conditions.map(conditionInput) : [],
			};

			inputGroup.Groups.concat(inputGroup.Conditions).forEach(function (child) {
				child.LogicOperator = group.LogicalOperator;
			});
			return inputGroup;
		};

		const conditionInput = (condition) => {
			if (typeof condition !== 'object' || condition === null) {
				return {};
			}
			const mappedCondition = {
				Name: condition.Name,
				ConditionType: types[condition.Name]?.ConditionType,
				Operator: condition.Operator,
				Operators: types[condition.Name]?.Operators,
				Value: condition.Value,
			};

			if (mappedCondition.ConditionType === this.ConditionTypes.EnumerationReference) {
				mappedCondition.ReferenceTo = types[condition.Name].ReferenceTo;
				mappedCondition.ReferenceFilter = types[condition.Name].ReferenceFilter;
				mappedCondition.EnumLoader = this.localEnumLoader;
			}
			return mappedCondition;
		};

		return groupInput;
	}

	/*private*/
	builderOutput(builder) {
		return this.groupOutput(builder, true);
	}

	/*private*/
	groupOutput(group, isBuilder) {
		const conditions = group.conditions.map((condition) => condition.output);
		const groups = group.groups.filter((group) => typeof group.output.LogicalOperator !== 'undefined');
		let logicalOperator;
		if (group.conditions.length) {
			logicalOperator = group.conditions[0].logicOperator;
		} else if (groups.length) {
			logicalOperator = groups[0].logicOperator;
		}
		const output = typeof logicalOperator === 'undefined' ? {} : { Conditions: conditions, Groups: groups.map((group) => group.output), LogicalOperator: logicalOperator };
		return isBuilder ? JSON.stringify(output) : output;
	}

	/*private*/
	conditionOutput(condition) {
		return {
			Name: condition.name,
			Value: condition.value,
			Operator: condition.operatorValue,
		};
	}
}

angular.module('mx.SolutionBuilderAgent').service('mx.SolutionBuilderAgent.AzureAdConditionsService', AzureAdConditionsService);
